/**
 * 拼音查表与拼接（**纯函数、无依赖、离线**）。
 *
 * 数据来源见 `./pinyin-data.ts`（内置的 mozillazg/pinyin-data，MIT）。
 * 本文件只做三件事：
 *
 *   1. 把内置数据表解析成"字 → 主读音 / 备选读音"；
 *   2. `resolvePinyin()` —— **人工覆盖优先**的取值规则（唯一的取值口径）；
 *   3. `joinWordPinyin()` —— 词语拼音按字拼接（逐字查表）。
 *
 * ## 为什么"人工覆盖"是第一等公民
 *
 * 听写靠**读音**驱动孩子写字。读错音不是"体验差一点"，而是**直接教错**：
 * 把"长大"读成 cháng dà、把"银行"读成 yín xíng，孩子写出来的字就是错的。
 * 多音字的正确读音**由词决定**，而任何离线字表都不可能知道"这一次是哪个词"。
 * 所以：
 *
 *   · 每个字只给一个"最常用读音"当默认值，**不猜**；
 *   · `resolvePinyin()` 永远让 `override` 覆盖自动值（空字符串不算覆盖）；
 *   · 词语拼音按字拼，并允许**整词覆盖** —— 这是"按词定音"的唯一入口。
 *
 * ## 关于未收录的字
 *
 * 内置表覆盖《通用规范汉字表》BMP 内的字（约 7900 个），足以覆盖小学课本。
 * 但家长也可能录到一个表里没有的字（生僻姓氏、地名）。此时 `lookupPinyin()`
 * 返回空串，`joinWordPinyin()` 对那个字**留空**而不是编一个读音 ——
 * 空串在界面上是"待填"，编一个读音则是"错得看不出来"。
 */

import { PINYIN_TABLE_SOURCE } from './pinyin-data.js';

/** 单字读音：主读音 + 备选读音（都不含空格，每个元素是一个音节）。 */
export interface CharPinyin {
  /** 最常用读音（内置表第一项）。表里没有这个字时为空串。 */
  primary: string;
  /** 其余读音（多音字）。去重、不含 `primary`，可能是空数组。 */
  alternates: string[];
}

/** 解析后的查表结构。 */
interface PinyinIndex {
  primary: Map<string, string>;
  alternates: Map<string, string[]>;
}

let cachedIndex: PinyinIndex | null = null;

/**
 * 解析内置数据（只做一次，惰性）。
 *
 * 格式：换行分隔条目，逗号分隔条目内的字段，条目的第一个字段是字、
 * 其余是读音（第一个是主读音）。见 `pinyin-data.ts` 的头部说明。
 */
export function getPinyinIndex(): PinyinIndex {
  if (cachedIndex) return cachedIndex;

  const primary = new Map<string, string>();
  const alternates = new Map<string, string[]>();

  for (const rawLine of PINYIN_TABLE_SOURCE.split('\n')) {
    const line = rawLine.trim();
    if (line === '') continue;
    for (const [char, readings] of parseEntryLine(line)) {
      primary.set(char, readings[0]!);
      if (readings.length > 1) alternates.set(char, readings.slice(1));
    }
  }

  cachedIndex = { primary, alternates };
  return cachedIndex;
}

/**
 * 解析一行（含 1..N 个条目）。
 *
 * 一行的形状是 `字:读音[,读音...],字:读音,...` —— **条目之间与条目内的读音之间
 * 用的是同一个逗号**，所以不能简单按逗号切。这里的规则：
 *
 *   · 按逗号切成 token；
 *   · 带 `:` 的 token 是一个**新条目**（`:` 左边是那个字，右边是它的第一个读音）；
 *   · 不带 `:` 的 token 是**上一个条目的后续读音**。
 *
 * ⚠️ 不能用"逗号后第 2 个字符是不是 `:`"来判断条目边界：那个"字"可能是
 *   代理对（如 `𠅤`，2 个 UTF-16 码元），猜错位置会把一个合法条目切坏。
 *   上面这条规则只看 `:` 而 `:` 绝不会出现在读音里，因此是唯一无歧义的切法。
 */
function parseEntryLine(line: string): [string, string[]][] {
  const out: [string, string[]][] = [];
  for (const token of line.split(',')) {
    if (token === '') continue;
    const colon = token.indexOf(':');
    if (colon > 0) {
      out.push([token.slice(0, colon), [token.slice(colon + 1).trim()].filter((r) => r !== '')]);
    } else if (colon < 0 && out.length > 0) {
      const last = out[out.length - 1]!;
      last[1].push(token.trim());
    }
  }
  return out.filter(([, readings]) => readings.length > 0);
}

/** 内置表覆盖的字数（诊断/文档用）。 */
export function pinyinTableSize(): number {
  return getPinyinIndex().primary.size;
}

/**
 * 查一个字的读音。未收录返回 `{ primary: '', alternates: [] }`。
 *
 * 传入多字符（或空串）时同样返回空 —— 本函数只认单个汉字；
 * 词语请用 `joinWordPinyin()`。
 */
export function lookupPinyin(char: string): CharPinyin {
  if ([...char].length !== 1) return { primary: '', alternates: [] };
  const index = getPinyinIndex();
  const primary = index.primary.get(char);
  if (primary === undefined) return { primary: '', alternates: [] };
  return { primary, alternates: index.alternates.get(char) ?? [] };
}

/** 自动读音（只看内置表）。 */
export function autoPinyin(char: string): string {
  return lookupPinyin(char).primary;
}

/**
 * **唯一**的"这个字该读什么"的口径：人工覆盖优先。
 *
 * @param char     单个汉字
 * @param override 人工覆盖值（`null` / `undefined` / 空串 / 纯空白 都表示"没覆盖"）
 *
 * 刻意把"空串不算覆盖"写死：录入接口里清空一个拼音输入框，语义是
 * "恢复自动"，而不是"把这个字读成空"。两者若混起来，家长就再也回不到自动值了。
 */
export function resolvePinyin(char: string, override?: string | null): string {
  const manual = override?.trim() ?? '';
  if (manual !== '') return manual;
  return autoPinyin(char);
}

/** 自动值 + 是否被人工覆盖。 */
export function pinyinState(
  char: string,
  override?: string | null,
): { auto: string; effective: string; overridden: boolean } {
  const auto = autoPinyin(char);
  const manual = override?.trim() ?? '';
  const overridden = manual !== '';
  return { auto, effective: overridden ? manual : auto, overridden };
}

/** 合法的"一个音节"：只含拉丁字母与声调符号（不含空格、数字、大写）。 */
const SYLLABLE_RE = /^[a-z\u00e0-\u00ff\u0100-\u017f\u01cd-\u01dc\u1e00-\u1eff\u00fc]+$/;

/** 判断一个字符串是否是"一个合法音节"（界面校验与导入校验共用）。 */
export function isPinyinSyllable(text: string): boolean {
  return SYLLABLE_RE.test(text);
}

/** 按空白/连字符切音节（"zhǎng dà"、"zhǎng-dà" 都认）。 */
export function splitSyllables(pinyin: string): string[] {
  return pinyin
    .trim()
    .split(/[\s\-]+/)
    .filter((s) => s !== '');
}

/**
 * 校验一条（整词）拼音：音节数必须与字数一致，且每个音节形状合法。
 *
 * 为什么值得单独做：手工粘进来的词语拼音最常见的错就是**少一个音节**
 * （"蝴蝶"只写了 hú dié 的 hú），或者把声调符号打成了数字（hu2 die2）。
 * 这类错如果放过去，听写时会**少读一个字**，而界面上完全看不出来。
 */
export function validateWordPinyin(
  text: string,
  pinyin: string,
): { ok: true; syllables: string[] } | { ok: false; reason: string; syllables: string[] } {
  const chars = [...text];
  const syllables = splitSyllables(pinyin);
  if (syllables.length !== chars.length) {
    return {
      ok: false,
      reason: `音节数（${syllables.length}）与字数（${chars.length}）不一致`,
      syllables,
    };
  }
  const bad = syllables.find((s) => !isPinyinSyllable(s));
  if (bad !== undefined) {
    return { ok: false, reason: `「${bad}」不是合法的拼音音节`, syllables };
  }
  return { ok: true, syllables };
}

/**
 * 词语拼音：逐字查表拼接，**整词覆盖优先**。
 *
 * @param text     词语
 * @param override 整词拼音（人工覆盖）。给了就用它（原样返回 trim 后的值）。
 * @returns 音节间用空格分隔的拼音串；未收录的字**留空**（不会缩短音节数），
 *          整词都没有读音时返回空串。
 *
 * 例：
 *   joinWordPinyin('学习')          → 'xué xí'
 *   joinWordPinyin('长大')          → 'zhǎng dà'   // 默认取最常用读音
 *   joinWordPinyin('长大', 'cháng dà') → 'cháng dà' // 人工覆盖按词定音
 *   joinWordPinyin('银行')          → 'yín xíng'   // ← 默认值就是错的，必须能覆盖
 */
export function joinWordPinyin(text: string, override?: string | null): string {
  const manual = override?.trim() ?? '';
  if (manual !== '') return manual;
  const chars = [...text];
  const parts = chars.map((c) => autoPinyin(c));
  if (parts.every((p) => p === '')) return '';
  return parts.join(' ');
}

/**
 * 拆字：把一个词按字符拆成数组（保留顺序，不做去重 —— 位置有意义）。
 * 供服务层建立"字 ↔ 词"关联时使用。
 */
export function splitChars(text: string): string[] {
  return [...text.trim()];
}
